import ApiClient from "@/api/ApiClient";
import {
  AttendanceScheduleDailyResponse,
  AttendanceScheduleMonthlyListResponse,
  AttendanceScheduleDailyPartSchema,
  ChildDefaultScheduleResponse,
  ChildDefaultSchedulePartSchema,
  FindOrCreateWSDisplaySettingResponse,
  WorkSchedulePlacementSettingResponse,
  WorkSchedulePlacementSettingBody,
} from "chaild-api/lib";

export default class ApiShift {
  public static async listAttendanceSchedules({
    year,
    month,
    nurseryId,
    classId,
  }: {
    year: string | number;
    month: string | number;
    nurseryId: string | number;
    classId?: string | number;
  }) {
    try {
      const response = await ApiClient.get(
        `/attendance-schedule/${year}/${month}/children`,
        {
          nurseryId,
          classId,
        }
      );
      return response as AttendanceScheduleMonthlyListResponse;
    } catch {
      return null;
    }
  }

  public static async addDefaultAttendanceSchedules({
    year,
    month,
    nurseryId,
  }: {
    year: string | number;
    month: string | number;
    nurseryId: string | number;
  }) {
    try {
      const response = await ApiClient.post(
        `/attendance-schedule/${year}/${month}/children`,
        {
          nurseryId,
        }
      );
      return response as AttendanceScheduleMonthlyListResponse;
    } catch {
      return null;
    }
  }

  public static async updateChildAttendanceSchedule({
    year,
    month,
    date,
    childId,
    abbreviation,
    color,
    status,
    statusReason,
    statusComment,
    startMin,
    endMin,
  }: {
    year: string | number;
    month: string | number;
    date: string | number | null;
    childId: string | number;
    abbreviation?: string;
    color?: string;
    status?: "attending" | "isAbsent";
    statusReason?: string;
    statusComment?: string;
    startMin?: number;
    endMin?: number;
  }) {
    try {
      const response = await ApiClient.put(
        `/attendance-schedule/${year}/${month}/${date}/children/${childId}`,
        {
          date,
          abbreviation,
          color,
          status,
          statusReason,
          statusComment,
          startMin,
          endMin,
        }
      );
      return response as AttendanceScheduleDailyResponse;
    } catch {
      return null;
    }
  }

  public static async deleteChildAttendanceSchedule({
    year,
    month,
    date,
    childId,
    abbreviation,
    color,
    status,
    statusReason,
    statusComment,
    startMin,
    endMin,
  }: {
    year: string | number;
    month: string | number;
    date: string | number | null;
    childId: string | number;
    abbreviation?: string;
    color?: string;
    status?: "attending" | "isAbsent";
    statusReason?: string;
    statusComment?: string;
    startMin?: number;
    endMin?: number;
  }) {
    try {
      const response = await ApiClient.put(
        `/attendance-schedule/${year}/${month}/${date}/children/${childId}/delete`,
        {
          date,
          abbreviation,
          color,
          status,
          statusReason,
          statusComment,
          startMin,
          endMin,
        }
      );
      return response as AttendanceScheduleDailyResponse;
    } catch {
      return null;
    }
  }

  public static async updateChildAttendanceMonthlySchedule({
    year,
    month,
    childId,
    dailySchedules,
  }: {
    year: number | string;
    month: number | string;
    childId: number | string;
    dailySchedules: AttendanceScheduleDailyPartSchema[];
  }) {
    try {
      const response = await ApiClient.put(
        `/attendance-schedule/${year}/${month}/children/${childId}`,
        {
          dailySchedules,
        }
      );
      return response as AttendanceScheduleDailyResponse;
    } catch {
      return null;
    }
  }

  public static async getDisplaySetting({
    nurseryId,
  }: {
    nurseryId: number;
  }) {
    try {
      const response = await ApiClient.get(
        `/work-schedule/workScheduleDisplaySetting`,
        {
          nurseryId,
        }
      );
      return response as FindOrCreateWSDisplaySettingResponse;
    } catch {
      return null;
    }
  }

  public static async updateDisplaySetting({
    nurseryId,
    requisiteGovExpected,
    requisiteExpected,
    sufficientRequisite,
    requisiteGovActual,
    requisiteActual,
    sufficientActual,
    excessOrDeficiencyExpected,
    excessOrDeficiencyActual,
    attendanceAge0,
    attendanceAge1,
    attendanceAge2,
    attendanceAge3,
    attendanceAge4
  }: {
    nurseryId: number;
    requisiteGovExpected: boolean | null;
    requisiteExpected: boolean | null;
    sufficientRequisite: boolean | null;
    requisiteGovActual: boolean | null;
    requisiteActual: boolean | null;
    sufficientActual: boolean | null;
    excessOrDeficiencyExpected: boolean | null;
    excessOrDeficiencyActual: boolean | null;
    attendanceAge0: boolean | null;
    attendanceAge1: boolean | null;
    attendanceAge2: boolean | null;
    attendanceAge3: boolean | null;
    attendanceAge4: boolean | null;
  }) {
    try {
      const response = await ApiClient.get(
        `/work-schedule/workScheduleDisplaySetting`,
        {
          nurseryId: nurseryId,
          requisiteGovExpected: requisiteGovExpected ?? null,
          requisiteExpected: requisiteExpected ?? null,
          sufficientRequisite: sufficientRequisite ?? null,
          requisiteGovActual: requisiteGovActual ?? null,
          requisiteActual: requisiteActual ?? null,
          sufficientActual: sufficientActual ?? null,
          excessOrDeficiencyExpected: excessOrDeficiencyExpected ?? null,
          excessOrDeficiencyActual: excessOrDeficiencyActual ?? null,
          attendanceAge0: attendanceAge0 ?? null,
          attendanceAge1: attendanceAge1 ?? null,
          attendanceAge2: attendanceAge2 ?? null,
          attendanceAge3: attendanceAge3 ?? null,
          attendanceAge4: attendanceAge4 ?? null
        }
      );
      return response as FindOrCreateWSDisplaySettingResponse;
    } catch {
      return null;
    }
  }

  public static async getPlacementSetting({
    nurseryId,
  }: {
    nurseryId: string | number;
  }) {
    try {
      const response = await ApiClient.get(
        `/work-schedule/${nurseryId}/settings/placement`,
        null
      );
      return response as WorkSchedulePlacementSettingResponse;
    } catch {
      return null;
    }
  }

  public static async updatePlacementSetting({
    nurseryId,
    body,
  }: {
    nurseryId: string | number;
    body: WorkSchedulePlacementSettingBody;
  }) {
    try {
      const response = await ApiClient.put(
        `/work-schedule/${nurseryId}/settings/placement`,
        body
      );
      return response as WorkSchedulePlacementSettingResponse;
    } catch {
      return null;
    }
  }

  public static async getChildDefaultSchedule(childId: number) {
    try {
      const response = await ApiClient.get(
        `/attendance-schedule/children`,
        {
          childId
        }
      );
      return response as ChildDefaultScheduleResponse;
    } catch {
      return null;
    }
  }

  public static async updateChildDefaultSchedule(
    arrayAddChildDefaultSchedule: {
      childId: number;
      Mins: ChildDefaultSchedulePartSchema;
    }
  ) {
     try {
       const response = await ApiClient.put(
         `/attendance-schedule/children`,
         arrayAddChildDefaultSchedule
        );
       return response as ChildDefaultScheduleResponse;
     } catch {
       return null;
     }
  }

  public static async deleteChildDefaultSchedule(
    arrayAddChildDefaultSchedule: {
      childId: number;
      Mins: ChildDefaultSchedulePartSchema;
    }
  ) {
     try {
       const response = await ApiClient.put(
         `/attendance-schedule/children`,
         arrayAddChildDefaultSchedule
        );
       return response as ChildDefaultScheduleResponse;
     } catch {
       return null;
     }
  }
}

